/**
 * CanvasEditor Component (Stories 5.31, 5.39)
 *
 * Extracted from generate.tsx CanvasLayoutEditor.
 * Contains all canvas editing logic: widget add/move/resize/delete/duplicate,
 * RGL layout orchestration, and keyboard shortcuts.
 *
 * Story 5.39: Migrated from @dnd-kit to react-grid-layout.
 * RGL handles drag/drop/resize natively. External drops from the palette
 * use HTML5 native drag + RGL's onDrop API.
 *
 * Used by the editor route ($reportId.edit.tsx).
 * The right sidebar (widget palette / config panel) is rendered separately
 * via EditorRightSidebar in the layout.
 */

import { useState, useCallback, useMemo, useEffect, useRef, type CSSProperties } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CaretLeft, X } from "@phosphor-icons/react";
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@provenleap/ui";
import { ReportCanvas } from "./ReportCanvas";
import { WidgetCategorySidebar } from "./WidgetPalette";
import { getCategoryPickerConfig, shouldFloat } from "./categoryPickerRegistry";
import type { WidgetType } from "./WidgetPalette";
import { isPresetType } from "./WidgetPalette";
import { WidgetConfigPanel, WIDGET_TYPE_INFO } from "./WidgetConfigPanel";
import { WidgetCommandPalette } from "./WidgetCommandPalette";
import {
  getNextAvailablePosition,
  calculateGridPlacement,
  getSmartDefaults,
  MAX_WIDGETS,
} from "../../lib/gridUtils";
import type { ValidColSpan, SmartDefaultWidgetType } from "../../lib/gridUtils";
import type { WidgetConfig } from "../../lib/templateUtils";
import { generateMockData } from "../../lib/mockWidgetData";
import type { WidgetData } from "../../lib/widgetData";
import { widgetHasDataBinding } from "../../lib/widgetCompatibility";
import { useAvailableMetrics } from "../../hooks/useAvailableMetrics";
import { useReportData } from "../../hooks/useReportData";
import { useRollupData } from "../../hooks/useRollupData";
import { useDebounce } from "../../hooks/useDebounce";
import { isClientConnected } from "../../lib/reportClientSource";
import { getRegistryForPlatform, ADAPTER_META } from "@provenleap/integrations";
import type { MetricDefinition } from "@provenleap/integrations";
import { resolveDefaultMetric, applyDefaultMetricToConfig } from "../../lib/metricCompatibility";
import { parseDragPayload } from "../../lib/dragPayload";
import { useToast } from "../../hooks/useToast";
import { useReportDnd } from "./ReportDndProvider";
import type { PaletteDragData, GridCell } from "../../lib/reportDnd";

/** `getRegistryForPlatform`'s `Platform` param, derived from its signature so we
 *  don't pull `@provenleap/db` (not a direct apps/web dep) in just for the type.
 *  The picker's `integrationId` slug IS a Platform value when it comes from a
 *  connected integration (correction #7); the cast is checked at runtime by the
 *  registry returning `undefined` for an unknown slug. */
type RegistryPlatform = Parameters<typeof getRegistryForPlatform>[0];
import { Toast } from "../clients/Toast";
import { getGridDimensions } from "@provenleap/types/report-rendering";
import { expandPreset, PRESET_LABELS } from "../../lib/widgetPresets";
import type { PresetWidget } from "../../lib/widgetPresets";
import type { PresetType } from "./WidgetPalette";

// ── Default Widget Configs ──────────────────────────────────────────
// Moved to the leaf `canvasWidgetDefaults` module (Story 37.3) so the Phase-2
// category panels can seed their drafts from the same defaults without an import
// cycle. Imported for this file's own use AND re-exported so every existing
// `from "./CanvasEditor"` importer keeps working — values byte-for-byte unchanged.
import { DEFAULT_WIDGET_TITLES, DEFAULT_WIDGET_CONFIGS, nextWidgetTitle } from "./canvasWidgetDefaults";
export { DEFAULT_WIDGET_TITLES, DEFAULT_WIDGET_CONFIGS };

/** Valid droppable widget types = the keys of `DEFAULT_WIDGET_CONFIGS`. */
const DROPPABLE_WIDGET_TYPES = new Set<string>(Object.keys(DEFAULT_WIDGET_CONFIGS));

/**
 * Recognises a drag-payload `type` that the canvas can actually add — a real
 * `WidgetType` (a `DEFAULT_WIDGET_CONFIGS` key) OR a preset type. Passed to
 * `parseDragPayload` so a foreign/stale type is rejected with a toast instead
 * of committing a broken, undefined-titled widget (Story 37.7 review #3).
 */
function isKnownDropType(type: string): boolean {
  return DROPPABLE_WIDGET_TYPES.has(type) || isPresetType(type);
}

/** Shifts all widgets down by the given number of rows. Used when adding a cover page. */
function shiftWidgetsDown(widgets: WidgetConfig[], rows: number): WidgetConfig[] {
  return widgets.map((w) => ({
    ...w,
    position: { ...w.position, row: w.position.row + rows },
  }));
}

// ── Zoom Wrapper (Story 5.38, Task 3) ────────────────────────────────

/**
 * Wraps the ReportCanvas with a SINGLE unified transform: translate(panX,panY)
 * scale(scale), transform-origin 0 0. Pan and zoom both manipulate the same
 * translate/scale (canonical zoom-to-pointer model), so the point under the
 * cursor stays fixed — no scroll/centering offset ambiguity.
 *
 * `panX/panY` are externally-controlled (route owns pan + zoom-to-cursor math).
 * `leftShift` (integration-picker push) is folded into the translate.
 */
function ZoomWrapper({
  zoomLevel,
  format = "document",
  leftShift = 0,
  panX = 0,
  panY = 0,
  children,
}: {
  zoomLevel: number;
  format?: "document" | "slide-deck";
  leftShift?: number;
  panX?: number;
  panY?: number;
  children: React.ReactNode;
}) {
  const dims = getGridDimensions(format);
  const contentWidthPx = dims.contentWidthPx;
  const scale = zoomLevel / 100;

  // Outer fills the scroll area (overflow:hidden on the parent) and provides the
  // positioned context. The leftShift (panel-open push) is applied on the OUTER
  // wrapper with its own eased transition so the canvas GLIDES left/right in sync
  // with the notch strip — without animating pan/zoom (which must track the
  // cursor 1:1, so they stay on the transition-free inner transform).
  const outerStyle: CSSProperties = {
    position: "relative",
    width: "100%",
    height: "100%",
    transform: `translateX(${-leftShift}px)`,
    transition: "transform 0.25s ease",
  };

  const zoomStyle: CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    width: contentWidthPx,
    transform: `translate(${panX}px, ${panY}px) scale(${scale})`,
    transformOrigin: "0 0",
  };

  return (
    <div data-testid="canvas-editor" style={outerStyle}>
      <div data-testid="zoom-wrapper" style={zoomStyle}>
        {children}
      </div>
    </div>
  );
}

// ── Props ──────────────────────────────────────────────────────────

export interface CanvasEditorProps {
  widgets: WidgetConfig[];
  onWidgetsChange: (next: WidgetConfig[] | ((prev: WidgetConfig[]) => WidgetConfig[])) => void;
  onWidgetsSilentChange: (next: WidgetConfig[] | ((prev: WidgetConfig[]) => WidgetConfig[])) => void;
  onUndo: () => void;
  onRedo: () => void;
  initialWidgets: WidgetConfig[];
  comparisonEnabled: boolean;
  reportDateRangeStart?: string;
  reportDateRangeEnd?: string;
  /** Comparison-period dates (Story 37.8). The route holds these on
   *  `dateRange.state.comparisonStart/End`; the hook needs them to key the
   *  comparison query. Only meaningful when `comparisonEnabled` is true. */
  comparisonStart?: string | null;
  comparisonEnd?: string | null;
  selectedClientName?: string;
  selectedClientLogoUrl?: string;
  /** Client ID — drives connected-platform metric narrowing for auto-bind
   *  on drop (Story 37.7) AND the live-data populate (Story 37.8). This is the
   *  EFFECTIVE client id the route derives as `searchClientId ?? loadedReport.clientId`
   *  (blocker #9) — NOT the raw search param. Optional: when absent, auto-bind
   *  falls back to the static registry and the canvas shows sample data. */
  clientId?: string;
  /** The client's connected platform slugs (Story 37.8, blocker #10). The
   *  SINGLE connected-decision source shared with the route's sample-data
   *  banner: the canvas renders LIVE data only when this is non-empty (and a
   *  clientId exists); otherwise it renders sample/mock data and the route shows
   *  the banner. Empty/absent ⇒ unconnected ⇒ mock. Keyed on the SAME effective
   *  clientId as the banner so the two never disagree. */
  connectedPlatforms?: string[];
  /**
   * Story 40-6 (AC5) — report kind. "rollup" routes the live-data populate
   * through `useRollupData(memberClientIds)` instead of `useReportData(clientId)`
   * and renders the aggregate-only palette. "client"/undefined is the existing
   * single-client path (byte-identical).
   */
  reportKind?: "client" | "rollup";
  /**
   * Story 40-6 (AC5) — the roll-up's resolved member client ids (the brand's
   * current members or the portfolio set). Only consulted when
   * reportKind === "rollup"; the single-client path ignores it. The member-union
   * `connectedPlatforms` is computed by the route, not here.
   */
  memberClientIds?: string[];
  format?: "document" | "slide-deck";
  /** Callback to notify parent of selected widget changes */
  onSelectedWidgetChange?: (widget: WidgetConfig | null) => void;
  /** Register an imperative deselect fn so the parent (e.g. the right-sidebar
   *  settings "Back" button) can clear the canvas selection — selection lives
   *  inside CanvasEditor, so it exposes the deselect upward (Epic 37). */
  registerDeselect?: (fn: () => void) => void;
  /** Symmetric to registerDeselect: register an imperative select-by-id fn so the
   *  parent (e.g. the pages-sidebar layers tree) can select a widget on the canvas. */
  registerSelect?: (fn: (widgetId: string) => void) => void;
  /** Register imperative duplicate/delete fns so the pages-sidebar layers-tree ⋮
   *  menu can act on a widget, reusing the SAME (tested) handlers the canvas uses. */
  registerWidgetActions?: (fns: { duplicate: (widgetId: string) => void; delete: (widgetId: string) => void }) => void;
  /** Canvas zoom level as percentage (Story 5.38) */
  zoomLevel?: number;
  /** Extra leftward shift (px) applied to the paper, e.g. when the floating
   *  integration picker is open, so the report nudges left to make room. */
  paperLeftShift?: number;
  /** Pan offset (px) for grab-to-pan + zoom-to-cursor. Route owns this. */
  panX?: number;
  panY?: number;
}

export function CanvasEditor({
  widgets,
  onWidgetsChange,
  onWidgetsSilentChange,
  onUndo,
  onRedo,
  initialWidgets,
  comparisonEnabled,
  reportDateRangeStart,
  reportDateRangeEnd,
  comparisonStart,
  comparisonEnd,
  selectedClientName,
  selectedClientLogoUrl,
  clientId,
  connectedPlatforms,
  reportKind = "client",
  memberClientIds,
  format = "document",
  onSelectedWidgetChange,
  registerDeselect,
  registerSelect,
  registerWidgetActions,
  zoomLevel = 100,
  paperLeftShift = 0,
  panX = 0,
  panY = 0,
}: CanvasEditorProps) {
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  // The red data-error notice is dismissable: a persistent fetch failure (e.g.
  // no live backend in dev) would otherwise pin it on screen forever. We key the
  // dismissal on the message text so a NEW/different failure re-surfaces, but the
  // same standing error stays hidden once the user closes it.
  const [dismissedErrorMessage, setDismissedErrorMessage] = useState<string | null>(null);

  // Expose an imperative deselect upward (for the sidebar settings "Back" button).
  useEffect(() => {
    registerDeselect?.(() => setSelectedWidgetId(null));
  }, [registerDeselect]);

  // Symmetric select-by-id, for the pages-sidebar layers tree.
  useEffect(() => {
    registerSelect?.((widgetId: string) => setSelectedWidgetId(widgetId));
  }, [registerSelect]);

  // Story 37.7: connected-platform metrics for auto-bind on drop. Destructure
  // the STABLE `byIntegration` sub-field — the hook's return object is a fresh
  // identity every render (correction #12), so we must never depend on it
  // directly. `byIntegration` only changes when query data resolves.
  const { byIntegration: connectedMetricsByPlatform } = useAvailableMetrics(clientId);
  const { toast: canvasToast, showToast: showCanvasToast } = useToast();

  // ── Live data wiring (Story 37.8, ADR-RE-5 + ADR-RE-7) ───────────────
  //
  // The SINGLE connected-decision source (blocker #10): the canvas renders LIVE
  // client numbers only when there's an effective clientId AND ≥1 connected
  // platform. Otherwise it renders sample/mock data and the route shows the
  // sample-data banner — both driven off the SAME `isClientConnected` helper so
  // they never disagree (no "mock shown with no banner" hole). `connectedPlatforms`
  // is keyed on the SAME effective clientId the banner uses (route-side).
  //
  // Story 40-6 (AC5): a ROLL-UP has no single clientId — it's "connected" when it
  // has ≥1 resolved member AND ≥1 platform in the member-union `connectedPlatforms`
  // the route passes. Both report kinds funnel through one boolean so the live↔mock
  // merge below stays a single decision point.
  const isRollup = reportKind === "rollup";
  const rollupMemberIds = useMemo(
    () => (isRollup ? memberClientIds ?? [] : []),
    [isRollup, memberClientIds],
  );
  const isConnectedClient = isRollup
    ? rollupMemberIds.length > 0 && (connectedPlatforms?.length ?? 0) > 0
    : isClientConnected(clientId, connectedPlatforms);

  // ADR-RE-7 layer 1 — debounce the QUERY-KEY INPUTS (date range + comparison),
  // NOT the widgets (Pre-mortem F: widgets have a fresh identity each render and
  // would never settle; the cost driver is the user SCRUBBING the date range).
  // Debounce a STABLE STRING derived from the key inputs (a primitive settles
  // reliably; an object would get a fresh identity each render and never settle),
  // then re-parse the matching dates with `JSON.parse` so the decode is robust to
  // ANY field content (no positional `split` that would misalign if a value ever
  // contained the delimiter — review R1). ~400ms trailing edge: long enough to
  // skip intermediate scrub ranges, short enough to feel responsive on landing.
  const liveStart = reportDateRangeStart ?? "";
  const liveEnd = reportDateRangeEnd ?? "";
  const liveComparisonStart = comparisonEnabled ? comparisonStart ?? "" : "";
  const liveComparisonEnd = comparisonEnabled ? comparisonEnd ?? "" : "";
  const rangeKey = JSON.stringify([liveStart, liveEnd, liveComparisonStart, liveComparisonEnd]);
  const debouncedRangeKey = useDebounce(rangeKey, 400);
  const [dStart, dEnd, dComparisonStart, dComparisonEnd] = useMemo(() => {
    // Defensive parse (review R2): the debounced value is always a freshly
    // JSON.stringified 4-tuple today, but a future `useDebounce` lazy-init or a
    // key-shape change must NEVER throw in render and take down the editor —
    // fall back to the LIVE (undebounced) values, which are always present.
    try {
      const parsed = JSON.parse(debouncedRangeKey) as unknown;
      if (Array.isArray(parsed) && parsed.length === 4 && parsed.every((p) => typeof p === "string")) {
        return parsed as [string, string, string, string];
      }
    } catch {
      // fall through to live values
    }
    return [liveStart, liveEnd, liveComparisonStart, liveComparisonEnd] as [string, string, string, string];
  }, [debouncedRangeKey, liveStart, liveEnd, liveComparisonStart, liveComparisonEnd]);
  const debouncedComparison = useMemo(
    () =>
      dComparisonStart && dComparisonEnd
        ? { start: dComparisonStart, end: dComparisonEnd }
        : null,
    [dComparisonStart, dComparisonEnd],
  );

  // Fetch live data via the EXISTING whole-report populate path (server dedups
  // per-platform internally — correction #2). Gated to `undefined` clientId when
  // the client is unconnected so the hook stays idle and NEVER fires a populate
  // for an unconnected client (which would return all-missing/error and defeat
  // the mock fallback — blocker #10 / Pre-mortem I). Feed the DEBOUNCED key.
  //
  // Story 40-6 (AC5): BOTH hooks are called UNCONDITIONALLY (Rules of Hooks) but
  // only ONE is ever enabled — the single-client hook with `undefined` clientId on
  // a roll-up, the roll-up hook with an empty member set on a single-client report.
  // Each no-ops when disabled (its own `enabled` gate), so the inactive one issues
  // no network call. We then pick the active result by `isRollup`.
  const singleClientResult = useReportData(
    !isRollup && isConnectedClient ? clientId : undefined,
    widgets,
    { start: dStart, end: dEnd },
    debouncedComparison,
  );
  const rollupResult = useRollupData(
    isRollup && isConnectedClient ? rollupMemberIds : [],
    widgets,
    { start: dStart, end: dEnd },
    debouncedComparison,
  );
  const {
    widgetData: liveWidgetData,
    platformStatus,
    isError: liveRequestFailed,
    isLoading: liveIsLoading,
    isFetching: liveIsFetching,
    isUpdating: liveIsUpdating,
    hasStaleComparison,
  } = isRollup ? rollupResult : singleClientResult;

  // Generate mock data for the unconnected / sample path (AC #1 fallback).
  const mockData = useMemo(() => generateMockData(widgets, comparisonEnabled), [widgets, comparisonEnabled]);

  // ── Live ↔ mock merge (AC #1) — the ONE tested decision point ─────────
  // Connected client → real numbers; unconnected (no clientId / no connected
  // platforms) OR a whole-request HTTP failure → sample/mock + banner. A
  // whole-request failure (non-200) flips `liveRequestFailed` and drops the
  // keepPreviousData placeholder (verified v5 behavior, Story 37.8 discovery),
  // so falling back to mock here keeps the canvas populated rather than blank.
  // Per-platform failures (server 200 w/ error status) do NOT flip this — they
  // surface in the stale/error banner via `platformStatus` (AC #5).
  //
  // PER-WIDGET fallback: on the connected path a widget shows SAMPLE data when it
  // either has NO data-source binding (e.g. a Line Chart just dropped — the server
  // can't resolve it) OR its live envelope came back with an ERROR (OAuth expired,
  // platform down, etc.). Either way the editor shows a usable preview instead of a
  // red message; the report-level banner (dataNotice) still flags that live data
  // failed, so the problem isn't silently hidden. Widgets with healthy live data
  // keep their real numbers.
  const canvasData = useMemo(() => {
    if (!isConnectedClient || liveRequestFailed) return mockData;
    const merged: Record<string, WidgetData> = { ...liveWidgetData };
    for (const widget of widgets) {
      const mock = mockData[widget.id];
      if (!mock) continue;
      const live = liveWidgetData[widget.id];
      const unbound = !widgetHasDataBinding(widget.config as Record<string, unknown>);
      const liveErrored = !!live?.error;
      if (unbound || liveErrored) {
        merged[widget.id] = mock;
      }
    }
    return merged;
  }, [isConnectedClient, liveRequestFailed, liveWidgetData, mockData, widgets]);

  // ── Canvas-level stale / cache / error affordance (AC #5) ─────────────
  // Surfaces server-reported freshness/error WITHOUT threading a prop through
  // ReportCanvas → WidgetRenderer into 20 widget components (correction #7 —
  // the signed-off shell is out of scope). Reuses the platformStatus +
  // hasStaleComparison the hook already returns. Only meaningful on the
  // connected path; the unconnected path shows the route's sample banner.
  const dataNotice = useMemo<{ kind: "error" | "stale" | "info"; message: string } | null>(() => {
    if (!isConnectedClient) return null;
    // Friendly platform name (ADAPTER_META) instead of the raw slug (review R1).
    const friendly = (slug: string): string =>
      ADAPTER_META[slug as keyof typeof ADAPTER_META]?.displayName ?? slug;
    // WHOLE-REQUEST failure (non-200): the query errored and the keepPreviousData
    // placeholder was dropped, so `platformStatus` is empty and `canvasData` fell
    // back to mock. Without this branch the user would see fabricated sample
    // numbers presented as real, with NO indication anything failed (review R1 —
    // the headline finding). Surface it explicitly.
    if (liveRequestFailed) {
      return {
        kind: "error",
        message: "Couldn't load live data right now — showing sample numbers. We'll retry automatically.",
      };
    }
    const statuses = Object.entries(platformStatus);
    // `error` = a genuine FETCH FAILURE (transient, retryable) → destructive notice.
    // `missing` = the platform simply isn't connected (a normal config state, NOT
    // a failure — review R3) → softer, non-destructive "connect" info notice. The
    // two must NOT be conflated: a widget bound to an unconnected platform on an
    // otherwise-healthy canvas should not show a permanent red "Couldn't load".
    const errored = statuses.filter(([, ps]) => ps.status === "error");
    if (errored.length > 0) {
      return {
        kind: "error",
        message:
          errored.length === 1
            ? `Couldn't load data for ${friendly(errored[0]![0])}. Showing what we have.`
            : `Couldn't load data for ${errored.length} integrations. Showing what we have.`,
      };
    }
    const missing = statuses.filter(([, ps]) => ps.status === "missing");
    if (missing.length > 0) {
      return {
        kind: "info",
        message:
          missing.length === 1
            ? `Connect ${friendly(missing[0]![0])} to see live data for that widget.`
            : `Connect ${missing.length} more integrations to see live data for every widget.`,
      };
    }
    // Stale notice fires ONLY on genuinely STALE data (or a stale comparison) —
    // NOT on a bare `fromCache` hit (review R3): a within-soft-TTL cache hit IS
    // fresh and is NOT being refreshed in the background (that's the whole point
    // of the raised staleTime), so "refreshing in the background" would lie.
    const anyStale = statuses.some(([, ps]) => ps.stale);
    if (anyStale || hasStaleComparison) {
      return {
        kind: "stale",
        message: "Showing recently cached numbers — refreshing in the background.",
      };
    }
    return null;
  }, [isConnectedClient, liveRequestFailed, platformStatus, hasStaleComparison]);

  // "updating…" affordance: shown when a REFETCH is in flight while prior data
  // stays on screen — NOT on the genuine first load (review R2: the first load
  // already shows widget loading-skeletons, so a "Updating…" pill on top of them
  // is double-chrome and misleading — nothing is being "updated", it's loading
  // from scratch). `liveIsUpdating` (= isFetching && isPlaceholderData) is the
  // keep-previous refetch; we also accept a bare `liveIsFetching` ONCE there is
  // already data on screen (`!liveIsLoading`) — the first-fetch-after-a-cached-
  // landing case. We do NOT show it for the bare debounce window (a cached-fresh
  // landing fires no fetch, so flashing it would be a lie — review R1).
  const isUpdatingData =
    isConnectedClient && (liveIsUpdating || (liveIsFetching && !liveIsLoading));

  // Selected widget for config panel
  const selectedWidget = useMemo(
    () => widgets.find((w) => w.id === selectedWidgetId) ?? null,
    [widgets, selectedWidgetId]
  );

  // Notify parent of selected widget changes
  useEffect(() => {
    onSelectedWidgetChange?.(selectedWidget);
  }, [selectedWidget, onSelectedWidgetChange]);

  // ── Handlers ────────────────────────────────────────────────────

  const handleWidgetSelect = useCallback((widgetId: string) => {
    // Always select (no toggle) — clicking a widget that's already selected keeps
    // it selected so the edit panel stays open instead of closing.
    setSelectedWidgetId(widgetId);
  }, []);

  /**
   * Story 37.7: candidate metrics for a platform, in adapter declaration
   * order, for the auto-bind heuristic. Option B precedence: the client's
   * CONNECTED metrics (real, server-backed) win; the static registry is the
   * synchronous fallback so a drop never blocks on the in-flight schema query.
   * Memoized on the STABLE `connectedMetricsByPlatform` (correction #12).
   */
  const resolveCandidateMetrics = useCallback(
    (platform: string): MetricDefinition[] => {
      // `byIntegration` is cast from an unvalidated schema response
      // (`useAvailableMetrics` does `json.data as IntegrationMetricRegistry`),
      // so guard `metrics` being absent/non-array — a partial payload must not
      // throw a TypeError mid-drop (review finding #4). Use optional chaining.
      const connected = connectedMetricsByPlatform[platform];
      if (connected?.metrics?.length) return connected.metrics;
      const registry = getRegistryForPlatform(platform as RegistryPlatform);
      return registry?.metrics ?? [];
    },
    [connectedMetricsByPlatform],
  );

  const handleWidgetAdd = useCallback((type: WidgetType, colSpan?: ValidColSpan, rowSpan?: number, row?: number, col?: number, integrationId?: string, initialConfig?: Record<string, unknown>) => {
    // PURE updater (React requirement): the function passed to a state setter
    // must NOT call other setters / fire toasts — React runs it during the
    // render phase, so `setSelectedWidgetId`/`showCanvasToast` inside it tripped
    // the "update a component (CanvasEditor) while rendering ReportEditorPage"
    // warning on every add (the committing updater lives in ReportEditorPage's
    // useReportPages). We compute the add's OUTCOME into `outcome` (no setState),
    // then apply the selection + toast AFTER `onWidgetsChange` returns.
    //
    // The new widget id is generated up-front (it doesn't depend on `prev`) so
    // the post-update selection can reference it. The updater is now idempotent
    // under React's StrictMode double-invoke (it sets `outcome` to the same value
    // and returns a value derived purely from `prev`); the side-effects run once.
    const newId = crypto.randomUUID();
    let outcome: { added: boolean; toast?: { kind: "error"; message: string } } = { added: false };

    onWidgetsChange((prev) => {
      if (prev.length >= MAX_WIDGETS) {
        outcome = { added: false, toast: { kind: "error", message: `Maximum ${MAX_WIDGETS} widgets per report.` } };
        return prev;
      }

      if (type === "cover-page" && prev.some((w) => w.type === "cover-page")) {
        outcome = { added: false, toast: { kind: "error", message: "Only one cover page per report." } };
        return prev;
      }

      const defaults = getSmartDefaults(type as SmartDefaultWidgetType);
      const effectiveColSpan = colSpan ?? defaults.colSpan;
      const effectiveRowSpan = rowSpan ?? defaults.rowSpan;

      // Drag-carried config (content-block cards) seeds over the type defaults —
      // same precedence as the category panels' configure-then-add path.
      let baseConfig: Record<string, unknown> = { ...DEFAULT_WIDGET_CONFIGS[type], ...initialConfig };
      if (integrationId) {
        baseConfig.integrationId = integrationId;
        // Story 37.7 auto-bind (ADR-RE-4): the platform-widget payload's
        // `integrationId` IS the platform slug (correction #7). Resolve the
        // representative default metric for this (platform, widget type) and
        // seed it into the per-drop config using the per-type key (AC #3), so
        // the widget lands populated on first paint — never blank. A missing
        // platform / no compatible metric leaves the type's blank default in
        // place (last resort, never a crash).
        const candidateMetrics = resolveCandidateMetrics(integrationId);
        const defaultMetric = resolveDefaultMetric(type, candidateMetrics);
        baseConfig = applyDefaultMetricToConfig(
          baseConfig,
          type,
          defaultMetric
            ? { key: defaultMetric.key, label: defaultMetric.label, integrationId }
            : undefined,
        );
      }

      if (type === "cover-page") {
        const position = { row: 0, col: 0, colSpan: 12 as ValidColSpan, rowSpan: 3 };
        const newWidget: WidgetConfig = {
          id: newId,
          type,
          title: DEFAULT_WIDGET_TITLES[type],
          position,
          config: baseConfig,
        };
        const shifted = shiftWidgetsDown(prev, 3);
        outcome = { added: true };
        return [newWidget, ...shifted];
      }

      const hasCoverPage = prev.some((w) => w.type === "cover-page");
      const reservedRows = hasCoverPage ? 3 : 0;

      let position;
      if (row !== undefined && col !== undefined) {
        const effectiveRow = reservedRows > 0 && row < reservedRows ? reservedRows : row;
        position = { row: effectiveRow, col, colSpan: effectiveColSpan, rowSpan: effectiveRowSpan };
      } else {
        const matrix = calculateGridPlacement(prev);
        const pos = getNextAvailablePosition(matrix, effectiveColSpan, effectiveRowSpan, reservedRows);
        position = { row: pos.row, col: pos.col, colSpan: effectiveColSpan, rowSpan: effectiveRowSpan };
      }

      const autoSizeTypes = new Set(["text-block", "data-table"]);
      const newWidget: WidgetConfig = {
        id: newId,
        type,
        title: nextWidgetTitle(type, prev),
        position,
        config: baseConfig,
        ...(autoSizeTypes.has(type) ? { autoSize: true } : {}),
      };
      outcome = { added: true };
      return [...prev, newWidget];
    });

    // Side-effects AFTER the pure updater (no setState during render).
    if (outcome.added) {
      setSelectedWidgetId(newId);
    } else if (outcome.toast) {
      showCanvasToast(outcome.toast.kind, outcome.toast.message);
    }
  }, [onWidgetsChange, showCanvasToast, resolveCandidateMetrics]);

  const handleWidgetDelete = useCallback((widgetId: string) => {
    // When deleting the SELECTED widget, immediately select a neighbour instead
    // of clearing selection — so the editor panel stays open (no close → canvas
    // re-shift → re-open jank). Pick the next widget at the deleted index, or the
    // previous one if it was last; clear only when nothing remains. The successor
    // is computed BEFORE the state change so it isn't read from a stale render.
    let nextSelectedId: string | null = selectedWidgetId;
    if (selectedWidgetId === widgetId) {
      const remaining = widgets.filter((w) => w.id !== widgetId);
      if (remaining.length === 0) {
        nextSelectedId = null;
      } else {
        const idx = widgets.findIndex((w) => w.id === widgetId);
        const successor = remaining[Math.min(idx, remaining.length - 1)];
        nextSelectedId = successor?.id ?? null;
      }
    }

    onWidgetsChange((prev) => prev.filter((w) => w.id !== widgetId));

    if (selectedWidgetId === widgetId) {
      setSelectedWidgetId(nextSelectedId);
    }
  }, [onWidgetsChange, selectedWidgetId, widgets]);

  const handleWidgetDuplicate = useCallback((widgetId: string) => {
    // Pure updater + post-update side-effects — same reasoning as handleWidgetAdd
    // (no setState/toast inside a state-updater that runs during render).
    const newId = crypto.randomUUID();
    let outcome: { added: boolean; toast?: { kind: "error"; message: string } } = { added: false };

    onWidgetsChange((prev) => {
      if (prev.length >= MAX_WIDGETS) return prev;
      const original = prev.find((w) => w.id === widgetId);
      if (!original) return prev;
      if (original.type === "cover-page") {
        outcome = { added: false, toast: { kind: "error", message: "Only one cover page per report." } };
        return prev;
      }
      const hasCoverPage = prev.some((w) => w.type === "cover-page");
      const reservedRows = hasCoverPage ? 3 : 0;
      const matrix = calculateGridPlacement(prev);
      const pos = getNextAvailablePosition(matrix, original.position.colSpan, original.position.rowSpan ?? 1, reservedRows);
      const duplicate: WidgetConfig = {
        ...original,
        id: newId,
        title: `${original.title} (copy)`,
        position: {
          ...original.position,
          row: pos.row,
          col: pos.col,
        },
      };
      outcome = { added: true };
      return [...prev, duplicate];
    });

    if (outcome.added) {
      setSelectedWidgetId(newId);
    } else if (outcome.toast) {
      showCanvasToast(outcome.toast.kind, outcome.toast.message);
    }
  }, [onWidgetsChange, showCanvasToast]);

  // Expose duplicate/delete upward so the pages-sidebar layers-tree ⋮ menu reuses
  // the SAME handlers (incl. delete's auto-select-neighbour behaviour).
  useEffect(() => {
    registerWidgetActions?.({ duplicate: handleWidgetDuplicate, delete: handleWidgetDelete });
  }, [registerWidgetActions, handleWidgetDuplicate, handleWidgetDelete]);

  // Auto-resize handler
  const autoResizeRafRef = useRef<number | null>(null);
  const pendingResizes = useRef<Map<string, number>>(new Map());

  const handleAutoResize = useCallback((widgetId: string, newRowSpan: number) => {
    pendingResizes.current.set(widgetId, newRowSpan);

    if (autoResizeRafRef.current) cancelAnimationFrame(autoResizeRafRef.current);
    autoResizeRafRef.current = requestAnimationFrame(() => {
      const resizes = new Map(pendingResizes.current);
      pendingResizes.current.clear();
      autoResizeRafRef.current = null;

      onWidgetsSilentChange((prev) => {
        let changed = false;
        const updated = prev.map((w) => {
          const newSpan = resizes.get(w.id);
          if (newSpan !== undefined && newSpan !== (w.position.rowSpan ?? 1)) {
            changed = true;
            return { ...w, position: { ...w.position, rowSpan: newSpan } };
          }
          return w;
        });
        if (!changed) return prev;
        return updated;
      });
    });
  }, [onWidgetsSilentChange]);

  const handleResetToTemplate = useCallback(() => {
    onWidgetsChange(initialWidgets);
    setSelectedWidgetId(null);
    setShowResetDialog(false);
  }, [onWidgetsChange, initialWidgets]);

  // ── Canvas move / resize (dnd-kit, Epic 37 — replaces RGL callbacks) ──────

  /** Commit a widget MOVE to a new grid cell (CanvasGrid computed the snapped
   *  cell from the drag delta, transform-safe). Pure updater: only the moved
   *  widget's row/col change; everything else is preserved. */
  const handleWidgetMove = useCallback(
    (widgetId: string, cell: { row: number; col: number }) => {
      onWidgetsChange((prev) =>
        prev.map((w) =>
          w.id === widgetId
            ? { ...w, position: { ...w.position, row: cell.row, col: cell.col } }
            : w,
        ),
      );
    },
    [onWidgetsChange],
  );

  /** Commit a widget RESIZE — colSpan already snapped to a valid span by
   *  CanvasGrid (applyResizeDelta + snapToValidSpan); rowSpan is free. */
  const handleWidgetResize = useCallback(
    (widgetId: string, colSpan: number, rowSpan: number) => {
      onWidgetsChange((prev) =>
        prev.map((w) =>
          w.id === widgetId
            ? { ...w, position: { ...w.position, colSpan: colSpan as ValidColSpan, rowSpan } }
            : w,
        ),
      );
    },
    [onWidgetsChange],
  );

  /** Handle preset expansion — adds multiple widgets in a single atomic state update (Story 12.2) */
  const handlePresetAdd = useCallback((presetType: PresetType, platform: string, integrationId: string) => {
    const expandedWidgets = expandPreset(presetType, platform, integrationId);
    const presetLabel = PRESET_LABELS[presetType] ?? presetType;

    // Pure updater + post-update side-effects (no setState/toast during render).
    // Also fixes a latent bug: the success select + "added" toast previously fired
    // UNCONDITIONALLY, so an over-budget preset showed BOTH the error toast and a
    // false success toast. Now they're gated on the actual outcome.
    let outcome: { added: boolean; toast: { kind: "error" | "success"; message: string } } | undefined;

    onWidgetsChange((prev) => {
      // Pre-flight: check if enough slots remain
      if (prev.length + expandedWidgets.length > MAX_WIDGETS) {
        const remaining = MAX_WIDGETS - prev.length;
        outcome = { added: false, toast: { kind: "error", message: `Only ${remaining} slot(s) remaining — ${presetLabel} needs ${expandedWidgets.length}.` } };
        return prev; // Add NOTHING — never add a partial preset
      }

      // Assign positions incrementally using the working array
      const hasCoverPage = prev.some((w) => w.type === "cover-page");
      const reservedRows = hasCoverPage ? 3 : 0;
      const workingWidgets = [...prev];
      const positioned: WidgetConfig[] = [];

      for (const widget of expandedWidgets) {
        const matrix = calculateGridPlacement(workingWidgets);
        const pos = getNextAvailablePosition(matrix, widget.colSpan as ValidColSpan, widget.rowSpan, reservedRows);
        const positionedWidget: WidgetConfig = {
          id: widget.id,
          type: widget.type,
          title: widget.title,
          position: { row: pos.row, col: pos.col, colSpan: widget.colSpan as ValidColSpan, rowSpan: widget.rowSpan },
          config: widget.config,
          ...(widget.autoSize ? { autoSize: true } : {}),
        };
        positioned.push(positionedWidget);
        workingWidgets.push(positionedWidget);
      }

      outcome = {
        added: true,
        toast: { kind: "success", message: `${presetLabel} added — ${expandedWidgets.length} widget${expandedWidgets.length > 1 ? "s" : ""}` },
      };
      return [...prev, ...positioned];
    });

    // `outcome` is assigned synchronously inside the updater above (React runs it
    // during this same call), so it's always defined here for a non-empty preset.
    const result = outcome as { added: boolean; toast: { kind: "error" | "success"; message: string } } | undefined;
    if (result?.added) {
      // Select the FIRST expanded widget
      setSelectedWidgetId(expandedWidgets[0]!.id);
    }
    if (result) {
      showCanvasToast(result.toast.kind, result.toast.message);
    }
  }, [onWidgetsChange, showCanvasToast]);


  // Empty-canvas drop: the empty state has no RGL grid (so no onDrop/layoutItem).
  // Parse the same palette payload and add the widget at the default position
  // (handleWidgetAdd places it via getNextAvailablePosition when no row/col given).
  const handleEmptyDrop = useCallback((e: DragEvent) => {
    const raw = e.dataTransfer?.getData("text/plain");
    if (!raw) return;
    const result = parseDragPayload(raw, isKnownDropType);
    if (!result.ok) {
      showCanvasToast("error", `Couldn't add that widget — ${result.reason}.`);
      return;
    }
    const data = result.payload;
    // Defer the add to the next microtask. handleWidgetAdd's updater also calls
    // setSelectedWidgetId/showCanvasToast; running it synchronously inside the
    // drop event (which fires mid-commit for the empty state) trips React's
    // "setState while rendering another component" warning. A microtask runs it
    // cleanly after the current render settles.
    queueMicrotask(() => {
      if (isPresetType(data.type)) {
        handlePresetAdd(data.type as PresetType, data.platform ?? "", data.integrationId ?? "");
        return;
      }
      handleWidgetAdd(
        data.type as WidgetType,
        data.colSpan as ValidColSpan,
        data.rowSpan,
        undefined,
        undefined,
        data.integrationId
      );
    });
  }, [handleWidgetAdd, handlePresetAdd, showCanvasToast]);

  // ── dnd-kit palette drop (Epic 37 canvas migration) ─────────────────
  // The DnD provider calls this on a palette drop onto the canvas, with the
  // target grid cell it tracked under the cursor (transform-safe). We reuse the
  // SAME handleWidgetAdd / handlePresetAdd add paths the old HTML5 drop used, so
  // auto-bind + guards + selection are identical — only the cell source differs.
  // A preset ignores the cell (it places its own multi-widget bundle). A null
  // cell (dropped with no resolved cell) falls back to auto-placement.
  const handlePaletteDndDrop = useCallback(
    (data: PaletteDragData, cell: GridCell | null) => {
      if (isPresetType(data.type)) {
        handlePresetAdd(data.type as PresetType, data.platform ?? "", data.integrationId ?? "");
        return;
      }
      handleWidgetAdd(
        data.type as WidgetType,
        data.colSpan as ValidColSpan,
        data.rowSpan,
        cell?.row,
        cell?.col,
        data.integrationId,
        data.config,
      );
    },
    [handleWidgetAdd, handlePresetAdd],
  );
  const { registerDropHandler } = useReportDnd();
  useEffect(() => {
    registerDropHandler(handlePaletteDndDrop);
  }, [registerDropHandler, handlePaletteDndDrop]);

  // ── Keyboard Shortcuts ──────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      const isMod = e.metaKey || e.ctrlKey;

      if (isMod && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        onUndo();
        return;
      }

      if (isMod && ((e.key === "z" && e.shiftKey) || e.key === "y")) {
        e.preventDefault();
        onRedo();
        return;
      }

      if (isMod && e.key === "d") {
        e.preventDefault();
        if (selectedWidgetId) handleWidgetDuplicate(selectedWidgetId);
        return;
      }

      if ((e.key === "Delete" || e.key === "Backspace") && selectedWidgetId) {
        e.preventDefault();
        handleWidgetDelete(selectedWidgetId);
        return;
      }

      if (e.key === "Escape" && selectedWidgetId) {
        e.preventDefault();
        setSelectedWidgetId(null);
        return;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onUndo, onRedo, selectedWidgetId, handleWidgetDuplicate, handleWidgetDelete]);

  return (
    <>
      {/* Story 37.8 — canvas-level data affordances (AC #3/#5). Fixed top-center
          over the canvas; uses existing design-system tokens (0.5px border,
          rounded-2xl, existing colors) per the UI directive — no per-widget
          chrome (correction #7). The unconnected/sample path shows the ROUTE's
          banner instead, so these only appear on the connected path.
          PRIORITY (review R2/R3): error > updating > stale > info. An ERROR
          notice is NEVER hidden by "Updating…" during a background retry (a
          persistent failure must stay visible); "Updating…" wins over the
          stale/info notices. `info` ("connect X to see live data") is the
          lowest-priority, non-destructive not-connected-platform hint. */}
      <AnimatePresence>
        {dataNotice?.kind === "error" && dataNotice.message !== dismissedErrorMessage ? (
          <motion.div
            key="data-error"
            className="absolute left-1/2 top-3 z-20 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-background shadow-xs"
            style={{ border: "0.5px solid var(--hairline)" }}
            data-testid="canvas-data-error"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <span className="text-[13px] text-destructive">{dataNotice.message}</span>
            <button
              type="button"
              onClick={() => setDismissedErrorMessage(dataNotice.message)}
              aria-label="Dismiss"
              data-testid="canvas-data-error-dismiss"
              className="-mr-1 ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-destructive/70 hover:bg-destructive/10 hover:text-destructive cursor-pointer transition-colors"
            >
              <X size={12} weight="bold" />
            </button>
          </motion.div>
        ) : isUpdatingData ? (
          <motion.div
            key="updating-affordance"
            className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-background shadow-xs"
            style={{ border: "0.5px solid var(--hairline)" }}
            data-testid="canvas-updating-indicator"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <span className="text-[13px] text-muted-foreground">Updating…</span>
          </motion.div>
        ) : dataNotice?.kind === "stale" ? (
          <motion.div
            key="data-stale"
            className="absolute left-1/2 top-3 z-20 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-background shadow-xs"
            style={{ border: "0.5px solid var(--hairline)" }}
            data-testid="canvas-data-stale"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <span className="text-[13px] text-muted-foreground">{dataNotice.message}</span>
          </motion.div>
        ) : dataNotice?.kind === "info" ? (
          <motion.div
            key="data-info"
            className="absolute left-1/2 top-3 z-20 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-background shadow-xs"
            style={{ border: "0.5px solid var(--hairline)" }}
            data-testid="canvas-data-info"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <span className="text-[13px] text-muted-foreground">{dataNotice.message}</span>
          </motion.div>
        ) : null}
      </AnimatePresence>
      <ZoomWrapper zoomLevel={zoomLevel} format={format} leftShift={paperLeftShift} panX={panX} panY={panY}>
        <ReportCanvas
          widgets={widgets}
          widgetData={canvasData}
          selectedWidgetId={selectedWidgetId}
          onWidgetSelect={handleWidgetSelect}
          onWidgetDelete={handleWidgetDelete}
          onWidgetDuplicate={handleWidgetDuplicate}
          onAutoResize={handleAutoResize}
          editable
          reportDateRangeStart={reportDateRangeStart}
          reportDateRangeEnd={reportDateRangeEnd}
          selectedClientName={selectedClientName}
          selectedClientLogoUrl={selectedClientLogoUrl}
          format={format}
          onWidgetMove={handleWidgetMove}
          onWidgetResize={handleWidgetResize}
          zoomLevel={zoomLevel}
          onEmptyQuickAdd={(type) => handleWidgetAdd(type)}
          onEmptyAddAt={(type, pos) =>
            handleWidgetAdd(
              type as WidgetType,
              pos.colSpan as ValidColSpan,
              pos.rowSpan,
              pos.row,
              pos.col,
            )
          }
          onEmptyDrop={handleEmptyDrop}
        />
      </ZoomWrapper>

      {/* Reset confirmation dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset to Template?</DialogTitle>
            <DialogDescription>
              This will restore the original template layout. Your changes can be undone with Ctrl+Z.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleResetToTemplate} data-testid="confirm-reset-btn">
              Reset Layout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toast toast={canvasToast} />

      {/* Cmd+K command palette for adding widgets */}
      <WidgetCommandPalette
        onAddWidget={(type, colSpan, rowSpan) => handleWidgetAdd(type, colSpan, rowSpan)}
      />
    </>
  );
}

// ── Right Sidebar for Editor ──────────────────────────────────────────

export interface EditorRightSidebarProps {
  activeCategory: string | null;
  setActiveCategory: (id: string | null) => void;
  /** Reset the Platform-Widgets drill-in when the active category changes
   *  (Story 37.2 — single source of selectedIntegration; the panel itself
   *  now floats via FloatingCategoryPicker, but the reset still lives here). */
  setSelectedIntegration: (id: string | null) => void;
  /** Connected integration platform IDs for the selected client (Story 12.2) */
  connectedIntegrations?: string[];
  /** Story 40-6 (AC6) — true for a roll-up report: hides the non-aggregatable
   *  category icons (custom-metrics + benchmarks). Forwarded to the sidebar. */
  restrictToRollup?: boolean;
}

export function EditorRightSidebar({
  activeCategory,
  setActiveCategory,
  setSelectedIntegration,
  connectedIntegrations,
  restrictToRollup,
}: EditorRightSidebarProps) {
  const [collapsed, setCollapsed] = useState(true);

  // Widget settings now float over the canvas (in the same slot as the category
  // picker) via FloatingWidgetSettings — NOT here. This sidebar is the category
  // icon column only.

  // Story 37.2: the category panels (integration two-state flow + the generic
  // card lists) now ALL float over the canvas via FloatingCategoryPicker —
  // there is exactly one render path for them. This sidebar renders only the
  // category icon column; clicking an icon toggles `activeCategory` (re-click
  // to close) and resets the Platform-Widgets drill-in.
  return (
    <div className="flex h-full">
      <motion.div
        className="flex flex-col bg-background overflow-y-hidden overflow-x-visible h-full"
        animate={{ width: collapsed ? 75 : 165 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <WidgetCategorySidebar
          activeCategory={activeCategory}
          onCategoryChange={(id) => {
            setActiveCategory(id === activeCategory ? null : id);
            setSelectedIntegration(null);
          }}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((v) => !v)}
          connectedIntegrations={connectedIntegrations}
          restrictToRollup={restrictToRollup}
        />
      </motion.div>
    </div>
  );
}

// ── Floating category picker — overlay card rendered over the canvas ──────
// Story 37.2: generalized from the old `FloatingIntegrationPicker`. ALL 8
// sidebar categories (the 7 generic ids + report-theme) now open through this
// one component, driven by CATEGORY_PICKER_REGISTRY — no per-category
// special cases. It sits on top of the dot-grid (the grid shows around it),
// anchored to the right of the canvas next to the widget icon sidebar. The
// panel width comes from the registry (standardized to 250), which is the same
// value that derives the canvas push + notch re-center, so they can't desync.

export interface FloatingCategoryPickerProps {
  /** Active sidebar category, or null when the picker is closed. */
  activeCategory: string | null;
  /** Platform Widgets drill-in state — owned by the host (single source). */
  selectedIntegration: string | null;
  setSelectedIntegration: (id: string | null) => void;
  /** Full integration objects for resolving preset integrationId bindings. */
  clientIntegrations?: { id: string; type: string }[];
  /**
   * Add a widget to the canvas with an optional initial config (Story 37.3).
   * Passed straight through to the active `panelComponent` as `onWidgetAdd` so
   * the configure-then-add panels (images/embeds) can attach a populated draft
   * config. The route wires this from `handleAddWidget`.
   */
  onWidgetAdd?: (type: WidgetType, initialConfig?: Record<string, unknown>) => boolean;
  /**
   * The report's EFFECTIVE client id (Story 18.4) — threaded to the active
   * panel so the GoalsCategoryPanel can fetch the client's saved goals/budgets.
   * undefined = unconnected/mock editor state.
   */
  clientId?: string;
  /**
   * Atomic multi-widget add (Story 18.4 AC #9) — passed to the panel as
   * `onPresetWidgetsAdd` for the combined budget/goal dashboard preset. The
   * route wires this from `handleAddPresetWidgets`.
   */
  onPresetWidgetsAdd?: (widgets: PresetWidget[]) => boolean;
  /**
   * Story 40-6 (AC6) — true for a ROLL-UP report. Threaded straight to the
   * active panel as `restrictToRollup`; the Platform-Widgets panel uses it to
   * show the aggregate-only generic widget list (no per-platform presets).
   */
  restrictToRollup?: boolean;
}

export function FloatingCategoryPicker({
  activeCategory,
  selectedIntegration,
  setSelectedIntegration,
  clientIntegrations,
  onWidgetAdd,
  clientId,
  onPresetWidgetsAdd,
  restrictToRollup,
}: FloatingCategoryPickerProps) {
  // `shouldFloat` is the single floats-or-not authority (registry presence).
  // When false (null / non-sidebar id / unknown), render nothing — the FMEA
  // safe lookup: a non-sidebar id leaking in or a future typo'd registry key
  // must not crash on `config.panelComponent` of undefined.
  const floats = shouldFloat(activeCategory);
  const config = floats ? getCategoryPickerConfig(activeCategory) : undefined;
  const Panel = config?.panelComponent;

  // Surface a mis-key in dev — once per transition into the bad state, not on
  // every render (the component re-renders on unrelated parent state: pan,
  // zoom, date range). A render-body console.warn would spam.
  useEffect(() => {
    if (activeCategory && !floats && import.meta.env.DEV) {
      console.warn(
        `[FloatingCategoryPicker] no registry entry for category "${activeCategory}" — rendering no panel.`,
      );
    }
  }, [activeCategory, floats]);

  return (
    <AnimatePresence>
      {/* `floats` already implies a non-null activeCategory + a config, but
          including `activeCategory` here lets TypeScript narrow it to string
          for the <Panel> prop below. */}
      {floats && activeCategory && Panel && (
        <motion.div
          key="floating-category-picker"
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 16 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          className="absolute right-4 z-40 flex flex-col overflow-hidden rounded-2xl bg-background shadow-xs"
          style={{ width: config?.panelWidth, top: "40px", height: "calc(100% - 62px)", border: "0.5px solid var(--hairline)" }}
        >
          {/* Key the panel by category so React remounts it on a category
              switch — otherwise the 6 card-list categories share one
              CardListPanel instance and WidgetCardsPanel's internal search
              state would leak across categories (the outer motion.div keeps a
              constant key so the open/close slide animation is preserved). */}
          <Panel
            key={activeCategory}
            activeCategory={activeCategory}
            selectedIntegration={selectedIntegration}
            setSelectedIntegration={setSelectedIntegration}
            clientIntegrations={clientIntegrations}
            onWidgetAdd={onWidgetAdd}
            clientId={clientId}
            onPresetWidgetsAdd={onPresetWidgetsAdd}
            restrictToRollup={restrictToRollup}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Floating widget settings — overlay card in the SAME slot as the category
// picker (Epic 37). When a widget is selected, its settings float over the canvas
// here (NOT all the way at the right edge). Same chrome + slide animation as
// FloatingCategoryPicker, with a Back button that deselects to return to the
// widget palette.

export interface FloatingWidgetSettingsProps {
  selectedWidget: WidgetConfig | null;
  onWidgetUpdate: (widgetId: string, changes: Partial<WidgetConfig>) => void;
  onDeselectWidget?: () => void;
  reportDateRangeStart?: string;
  reportDateRangeEnd?: string;
  selectedClientName?: string;
  clientId?: string;
}

export function FloatingWidgetSettings({
  selectedWidget,
  onWidgetUpdate,
  onDeselectWidget,
  reportDateRangeStart,
  reportDateRangeEnd,
  selectedClientName,
  clientId,
}: FloatingWidgetSettingsProps) {
  return (
    <AnimatePresence>
      {selectedWidget && (
        <motion.div
          key="floating-widget-settings"
          data-testid="floating-widget-settings"
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 16 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          // Same chrome as the widgets palette panel (250 wide, rounded-2xl, 0.5px
          // border, shadow-lg, top:40) — but height fits the content (the number of
          // fields the widget has) instead of filling the canvas, so a 3-field card
          // doesn't leave a tall empty box. It still caps at the available height
          // (maxHeight) and the body scrolls when a widget has many fields.
          className="absolute right-4 z-40 flex flex-col overflow-hidden rounded-2xl bg-background shadow-xs"
          // Same chrome as the widgets palette panel; height fits the content (the
          // number of fields), capped at the available height with the body scrolling.
          style={{ width: 250, top: "40px", maxHeight: "calc(100% - 62px)", border: "0.5px solid var(--hairline)" }}
        >
          {/* Header — back caret + the widget's icon + its type name (e.g. "# KPI Card"). */}
          <div className="px-4 pt-3 pb-2">
            <button
              type="button"
              data-testid="widget-settings-back"
              onClick={() => onDeselectWidget?.()}
              className="group flex items-center gap-2 text-foreground transition-colors cursor-pointer"
            >
              <CaretLeft size={16} className="shrink-0 text-foreground" />
              {(() => {
                const info = WIDGET_TYPE_INFO[selectedWidget.type];
                const Icon = info?.icon;
                return (
                  <>
                    {Icon && <Icon size={16} className="shrink-0 text-foreground" />}
                    <span className="truncate text-[13.5px] font-medium">{info?.label ?? selectedWidget.title}</span>
                  </>
                );
              })()}
            </button>
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-3">
            <WidgetConfigPanel
              widget={selectedWidget}
              onUpdate={onWidgetUpdate}
              reportDateRangeStart={reportDateRangeStart}
              reportDateRangeEnd={reportDateRangeEnd}
              selectedClientName={selectedClientName}
              clientId={clientId}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
